import { useEffect, useRef } from "react";
import submit from "submit-form";

/**
 * Polls the modified endpoint to detect meal-plan updates
 * and automatically refreshes the page when changes are detected
 *
 * @param {Object} options
 * @param {string} options.field - The field name to check in modified response (e.g., "meal-plan")
 * @param {number} options.interval - Polling interval in milliseconds (default: 5000)
 * @param {boolean} options.enabled - Whether polling is enabled (default: true)
 * @param {Function|boolean} options.shouldPoll - Optional function or boolean that indicates if polling should continue
 */
export function useModifiedPolling({
  field,
  shouldPoll,
  interval = 5000,
  enabled = true,
} = {}) {
  const lastModifiedRef = useRef(null);
  const pollingIntervalRef = useRef(null);
  const isRequestInProgressRef = useRef(false);
  const shouldPollRef = useRef(shouldPoll);

  useEffect(() => {
    shouldPollRef.current = shouldPoll;
  }, [shouldPoll]);

  const getShouldPoll = () => {
    if (shouldPollRef.current === undefined || shouldPollRef.current === null) {
      return true;
    }
    if (typeof shouldPollRef.current === "function") {
      return shouldPollRef.current();
    }
    return Boolean(shouldPollRef.current);
  };

  useEffect(() => {
    if (!enabled || typeof window === "undefined") {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
      return;
    }

    const checkForUpdates = async () => {
      const shouldContinuePolling = getShouldPoll();

      if (!shouldContinuePolling) {
        if (pollingIntervalRef.current) {
          clearInterval(pollingIntervalRef.current);
          pollingIntervalRef.current = null;
        }
        return;
      }

      if (isRequestInProgressRef.current) {
        return;
      }

      isRequestInProgressRef.current = true;

      try {
        const response = await fetch(`/actions/common/check-modified`, {
          credentials: "same-origin",
          headers: {
            "Content-Type": "application/json",
          },
        });

        if (!response.ok) {
          return;
        }

        const modified = await response.json();
        const currentModified = modified?.[field];

        if (lastModifiedRef.current === null) {
          lastModifiedRef.current = currentModified;
          return;
        }

        if (currentModified && lastModifiedRef.current) {
          const currentTime = new Date(currentModified).getTime();
          const lastTime = new Date(lastModifiedRef.current).getTime();

          if (currentTime > lastTime) {
            lastModifiedRef.current = currentModified;
            submit("/actions/refresh", {
              method: "GET",
              body: {},
            });
          }
        }
      } catch (error) {
        console.error("Error checking for modified updates:", error);
      } finally {
        isRequestInProgressRef.current = false;
      }
    };

    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }

    const shouldStartPolling = getShouldPoll();

    if (shouldStartPolling) {
      checkForUpdates();
      pollingIntervalRef.current = setInterval(checkForUpdates, interval);
    }

    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
      isRequestInProgressRef.current = false;
    };
  }, [interval, enabled, field]);

  useEffect(() => {
    if (!enabled || typeof window === "undefined") {
      return;
    }

    const shouldContinue = getShouldPoll();

    if (!shouldContinue) {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    } else if (!pollingIntervalRef.current) {
      const checkForUpdates = async () => {
        const shouldContinuePolling = getShouldPoll();

        if (!shouldContinuePolling) {
          if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current);
            pollingIntervalRef.current = null;
          }
          return;
        }

        if (isRequestInProgressRef.current) {
          return;
        }

        isRequestInProgressRef.current = true;

        try {
          const response = await fetch(`/actions/common/check-modified`, {
            credentials: "same-origin",
            headers: {
              "Content-Type": "application/json",
            },
          });

          if (!response.ok) {
            return;
          }

          const modified = await response.json();
          const currentModified = modified?.[field];

          if (lastModifiedRef.current === null) {
            lastModifiedRef.current = currentModified;
            return;
          }

          if (currentModified && lastModifiedRef.current) {
            const currentTime = new Date(currentModified).getTime();
            const lastTime = new Date(lastModifiedRef.current).getTime();

            if (currentTime > lastTime) {
              lastModifiedRef.current = currentModified;
              submit("/actions/refresh", {
                method: "GET",
                body: {},
              });
            }
          }
        } catch (error) {
          console.error("Error checking for modified updates:", error);
        } finally {
          isRequestInProgressRef.current = false;
        }
      };

      checkForUpdates();
      pollingIntervalRef.current = setInterval(checkForUpdates, interval);
    }
  }, [enabled, shouldPoll, interval, field]);
}
